import {
  Sparkles, ClipboardList, MessageCircle, Leaf, Compass,
  Calendar, ShieldCheck, HeartHandshake,
} from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, StepList,
  ClosingCta, ReassuranceRibbon,
} from "../components/TrustLayout";

/**
 * /how-it-works — calm, 6-step explainer. Schema: HowTo.
 */
export default function HowItWorksPage() {
  useSeo({
    title: "How Feel Greats works — Guided wellness in 6 calm steps",
    description:
      "Take the free assessment, get a personalised report, talk to a mentor, start slowly. A calm, no-pressure overview of how Feel Greats supports you.",
    canonical: `${PROD_DOMAIN}/how-it-works`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How Feel Greats works",
        "description": "A 6-step guided wellness journey — from assessment to ongoing mentor support.",
        "step": [
          { "@type": "HowToStep", "position": 1, "name": "Take the free assessment", "text": "Spend two minutes answering questions about your goals, sleep, stress, energy and history. No card required." },
          { "@type": "HowToStep", "position": 2, "name": "Get your personalised report", "text": "Your assessment becomes a calm, plain-English report with BMI, risk indicators and the most aligned next step." },
          { "@type": "HowToStep", "position": 3, "name": "Review your guided recommendations", "text": "We suggest one or two products at most — never a shopping basket. Each comes with a 'why we suggested this' explanation." },
          { "@type": "HowToStep", "position": 4, "name": "Talk to a wellness mentor", "text": "Optional, free, no pressure. A real human on WhatsApp answers your questions before you decide." },
          { "@type": "HowToStep", "position": 5, "name": "Start slowly", "text": "Most members begin with one product. We'd rather you build trust with a small change than overwhelm your body with a stack." },
          { "@type": "HowToStep", "position": 6, "name": "Continue with ongoing support", "text": "Check-ins at week 2, week 6 and quarterly. Pause, adjust or step away any time — we're here either way." },
        ],
      },
    ],
  });

  return (
    <TrustPage testid="how-it-works-page">
      <TrustHero
        eyebrow="How it works"
        headline="Six calm steps. No pressure."
        highlightWord="calm"
        body="Most wellness journeys start in panic — frantic Googling, conflicting advice, a half-empty shelf of supplements that didn't help. Feel Greats is built to feel like the opposite. Here's exactly how it works, start to finish."
        primaryCta={{ to: "/questionnaire", label: "Take the assessment now", testid: "how-hero-cta", icon: Sparkles }}
        secondaryCta={{ to: "/mentor-support", label: "Talk to a mentor first", testid: "how-hero-mentor" }}
        testid="how-hero"
      />

      <ReassuranceRibbon
        items={[
          { icon: ShieldCheck, title: "Free to start", body: "No card, no commitment — ever." },
          { icon: HeartHandshake, title: "Real humans", body: "A wellness mentor is one WhatsApp message away." },
          { icon: Leaf, title: "Calm pace", body: "Most members start with one product, not five." },
        ]}
        testid="how-ribbon"
      />

      {/* The 6 steps */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="how-steps-section">
        <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">The journey</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
          From "I don't know where to start" → "this feels manageable."
        </h2>
        <p className="mt-3 text-base text-emerald-900/75 max-w-2xl leading-relaxed">
          You can pause at any step. You can skip the mentor call. You can decide nothing is right and walk away. We've designed it that way on purpose.
        </p>
        <div className="mt-9">
          <StepList
            testid="how-steps"
            steps={[
              {
                icon: ClipboardList,
                title: "1. Take the free assessment.",
                body: "Two minutes, ten questions. We ask about your sleep, stress, energy, goals and any conditions or medications. No card required, no signup wall.",
                note: "Privacy-first — your data is encrypted and never shared.",
              },
              {
                icon: Compass,
                title: "2. Get your personalised report.",
                body: "Your answers become a calm, plain-English wellness report: BMI, risk indicators in the areas that matter most for your profile, and the single 'main problem' to focus on first. No fear-mongering — just clarity.",
              },
              {
                icon: Sparkles,
                title: "3. Review your guided recommendations.",
                body: "We suggest one or two products at most — never a basket. Each comes with a 'why we suggested this' explanation in plain language, plus the ingredient PDF and a calm note on what to expect in the first 6 weeks.",
              },
              {
                icon: MessageCircle,
                title: "4. Talk to a wellness mentor (optional, free).",
                body: "Want a human reality-check before you spend anything? Message our mentor on WhatsApp. They'll ask a few more questions and give you their honest read — sometimes that read is 'don't buy yet, try this habit first.'",
              },
              {
                icon: Leaf,
                title: "5. Start slowly.",
                body: "Most members start with one product and a small daily anchor (a glass of water with it, a 10-minute walk after). Big stacks rarely work. Small, repeatable rhythms do.",
                note: "Subscribe and save 10% — or just buy once. Always cancellable.",
              },
              {
                icon: Calendar,
                title: "6. Continue with ongoing support.",
                body: "We check in at week 2 (settling in), week 6 (the most common moment of subtle shift), and quarterly (rerun your assessment together). You can pause, adjust, or step away any time. There's never a sales pitch attached.",
              },
            ]}
          />
        </div>
      </section>

      <TrustSection
        eyebrow="The promise inside the steps"
        title="You're never doing this alone."
        body="Every step above has a human reachable on the other side of it. You can email, WhatsApp, or book a 15-minute mentor call — all included, no upsell."
        tone="amber"
        testid="how-promise"
      />

      <ClosingCta
        eyebrow="Ready when you are"
        title="It really does start with two quiet minutes."
        body="The assessment is free, anonymous if you want it to be, and gives you a useful read of where you are — even if you never buy anything."
      />
    </TrustPage>
  );
}
